import { forwardRef, useId } from "react";
import type { ReactNode, SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";

// 下拉選單。用原生 select,鍵盤與行動裝置的行為最可靠。

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  options: SelectOption[];
  /**
   * 標籤下方的說明文字。**與 `Input` 的同名屬性一模一樣** ——
   * 兩個元件的結構要完全對得起來,同一列裡混用時才不會一高一低。
   *
   * 橫排的篩選列上不要用它:有說明的那一格比別人高,整列就對不齊了
   * (說明放在整列下面)。這個屬性是給直排的表單用的。
   */
  hint?: ReactNode;
  error?: string | null;
  /**
   * 把錯誤訊息宣告成即時區域(`role="alert"`)。理由與 `Input` 的同名屬性相同:
   * 錯誤是別的欄位造成的、或在使用者碰到這一格之前就成立時,不宣告等於沒說。
   */
  errorRole?: "alert";
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, hint, error, errorRole, className = "", id, ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const hintId = hint ? `${selectId}-hint` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {label ? (
        <label htmlFor={selectId} className="text-sm font-medium text-[var(--text-2)]">
          {label}
        </label>
      ) : null}
      <div className="relative">
        <select
          {...rest}
          id={selectId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={
            "h-9 w-full min-w-0 appearance-none rounded-[var(--radius)] border bg-[var(--surface)] " +
            "px-2.5 pr-8 text-sm text-[var(--text)] " +
            (error ? "border-[var(--danger)] " : "border-[var(--border-strong)] ") +
            className
          }
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          size={16}
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
        />
      </div>
      {hint ? (
        <span id={hintId} className="text-xs text-[var(--text-3)]">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role={errorRole} className="text-xs text-[var(--danger)]">
          {error}
        </span>
      ) : null}
    </div>
  );
});
